import { PageHeading } from "@/components/ui/page";
import React, { useEffect, useMemo } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarRange, ChevronLeft, Lock, Printer, RefreshCw, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { useAuth } from "@/features/auth/contexts/AuthContext";
import { cn, formatDateTime } from "@/lib/utils";
import { ReportsFiltersBar } from "../components/ReportsFiltersBar";
import { NewBadge } from "../components/ReportRow";
import { useReportFilters } from "../hooks/useReportFilters";
import { useReportPrefs } from "../hooks/useReportPrefs";
import { canOpenReport, findReport } from "../catalog";
import { getDateRangeFromPreset, reportFiltersToParams, scopeFilters, toYmd } from "../types";

/** One report: its own filters, headline numbers, chart and table, with pin / refresh / print. */
const ReportViewerPage: React.FC = () => {
	const { reportId } = useParams<{ reportId: string }>();
	const location = useLocation();
	const { t } = useTranslation();
	const { user } = useAuth();
	const queryClient = useQueryClient();
	const { filters, setFilters, search } = useReportFilters();
	const { pinned, togglePin, recordOpen } = useReportPrefs();
	const report = findReport(reportId);
	const canOpen = Boolean(report && canOpenReport(user, report));

	const scoped = useMemo(() => scopeFilters(filters, report?.filters ?? []), [filters, report]);
	const params = useMemo(() => reportFiltersToParams(scoped), [scoped]);

	useEffect(() => {
		if (report && report.id === reportId && canOpen) recordOpen(report.id);
		// One open per report visit, not one per filter change.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [report?.id, canOpen]);

	if (!report) return <Navigate to={{ pathname: "/reports", search: location.search }} replace />;
	if (report.id !== reportId) {
		return <Navigate to={{ pathname: `/reports/${report.id}`, search: location.search }} replace />;
	}

	const title = t(`reports.catalog.${report.key}.title`);
	const Icon = report.icon;
	const Body = report.component;
	const isPinned = pinned.includes(report.id);
	const range = getDateRangeFromPreset(scoped);
	const hasPeriod = report.filters.includes("period");
	// "Custom" with a date missing (cleared input, pasted link) waits for both dates instead of loading all time.
	const needsDates = scoped.dateRangePreset === "custom" && !range.startDate;
	const rangePart =
		range.startDate && range.endDate
			? `${range.startDate}_${range.endDate}`
			: hasPeriod
				? t("reports.dateRanges.all")
				: toYmd(new Date());
	const exportName = `${title} ${rangePart}`;
	const related = (report.related ?? [])
		.map(id => findReport(id))
		.filter((r): r is NonNullable<typeof r> => Boolean(r && canOpenReport(user, r)));

	return (
		<div className="mx-auto max-w-[1400px] space-y-4 p-3 sm:p-6 print:max-w-none print:p-0">
			<nav aria-label={t("reports.viewer.breadcrumb")} className="text-sm print:hidden">
				<Link
					to={{ pathname: "/reports", search }}
					className="inline-flex items-center gap-1 rounded underline underline-offset-4 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
				>
					<ChevronLeft className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />
					{t("reports.viewer.allReports")}
				</Link>
				<span className="mx-2 text-muted-foreground/60" aria-hidden="true">
					/
				</span>
				<span className="text-muted-foreground">{t(`reports.groups.${report.group}.title`)}</span>
			</nav>

			<header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
				<div className="flex min-w-0 items-start gap-3">
					<span
						className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-primary/25 bg-card text-primary shadow-sm print:hidden"
						aria-hidden="true"
					>
						<Icon className="h-6 w-6" />
					</span>
					<div className="min-w-0">
						<PageHeading className="flex flex-wrap items-center gap-2">
							{title}
							{report.isNew && <NewBadge />}
						</PageHeading>
						<p className="mt-0.5 text-sm text-muted-foreground">
							{t(`reports.catalog.${report.key}.description`)}
						</p>
						<p className="mt-1 hidden text-sm print:block">
							{/* The period prints with the filter summary; reports without one are a snapshot of now. */}
							{!hasPeriod && `${t("reports.viewer.asOfNow")} · `}
							{t("reports.viewer.printedAt", { date: formatDateTime(new Date()) })}
						</p>
					</div>
				</div>
				{canOpen && (
					<div className="flex shrink-0 items-center gap-2 print:hidden">
						<Button
							variant="outline"
							size="sm"
							onClick={() => togglePin(report.id)}
							aria-pressed={isPinned}
							title={isPinned ? t("reports.home.unpin") : t("reports.home.pin")}
						>
							<Star
								className={cn("h-4 w-4 me-2", isPinned && "fill-amber-400 text-warning")}
								aria-hidden="true"
							/>
							{isPinned ? t("reports.viewer.pinned") : t("reports.viewer.pin")}
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={() => queryClient.invalidateQueries({ queryKey: ["report"] })}
							aria-label={t("common.refresh")}
							title={t("common.refresh")}
						>
							<RefreshCw className="h-4 w-4" />
						</Button>
						<Button variant="outline" size="sm" onClick={() => window.print()}>
							<Printer className="h-4 w-4 me-2" aria-hidden="true" />
							{t("reports.viewer.print")}
						</Button>
					</div>
				)}
			</header>

			{!canOpen ? (
				<div className="rounded-2xl border bg-card">
					<EmptyState
						icon={Lock}
						title={t("reports.viewer.noAccess")}
						description={t("reports.viewer.noAccessHint")}
					/>
				</div>
			) : (
				<>
					{report.filters.length > 0 && (
						<ReportsFiltersBar value={filters} onChange={setFilters} fields={report.filters} />
					)}
					{needsDates ? (
						<div className="rounded-2xl border bg-card">
							<EmptyState
								icon={CalendarRange}
								title={t("reports.dateRange.pickDates")}
								description={t("reports.dateRange.pickDatesHint")}
							/>
						</div>
					) : (
						<Body key={report.id} params={params} filters={scoped} exportName={exportName} />
					)}
					{related.length > 0 && (
						<section className="rounded-2xl border bg-card p-4 print:hidden">
							<h2 className="mb-3 text-sm font-semibold">{t("reports.viewer.related")}</h2>
							<div className="flex flex-wrap gap-2">
								{related.map(r => {
									const RelatedIcon = r.icon;
									return (
										<Link
											key={r.id}
											to={`/reports/${r.id}${search}`}
											className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors hover:border-primary/40 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
										>
											<RelatedIcon className="h-4 w-4 text-primary" aria-hidden="true" />
											{t(`reports.catalog.${r.key}.title`)}
										</Link>
									);
								})}
							</div>
						</section>
					)}
				</>
			)}
		</div>
	);
};

export default ReportViewerPage;
